import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Paper
} from '@mui/material';
import {
  Share as ShareIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  AdminPanelSettings as AdminIcon,
  EditOutlined as EditAccessIcon,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { addError, addInfo } from 'app/actions/snackbar/snackbar';
import { companiesSelector } from 'app/selectors/companiesSelector';
import { isUserSuperAdminSelector } from 'app/selectors/userSelectors';
import { AccessLevel } from '@/api-gen/api.schemas';
import type { RoleCompanyDto } from '@/api-gen/api.schemas';
import {
  useRolesGetRoleCompanies,
  useRolesUnshareRole,
  useRolesUpdateRoleCompanyAccess
} from '@/api-gen/api';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nCommonNs, I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces';

interface RoleSharedCompaniesDisplayProps {
  roleId?: number;
  roleName?: string | null;
  otherOrganizationsCount?: number;
  canManageSharing?: boolean;
  onRefresh?: () => void;
}

const RoleSharedCompaniesDisplay: React.FC<RoleSharedCompaniesDisplayProps> = ({
  roleId,
  roleName,
  otherOrganizationsCount,
  canManageSharing = false,
  onRefresh
}) => {
  const dispatch = useDispatch();
  const companies = useSelector(companiesSelector);
  const isSuperAdmin = useSelector(isUserSuperAdminSelector);
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor, I18nNamespace.Common]);

  const [selectedCompany, setSelectedCompany] = useState<RoleCompanyDto | null>(null);
  const [selectedAccess, setSelectedAccess] = useState<AccessLevel>(AccessLevel.CanRead);
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false);
  const [isUnshareDialogOpen, setIsUnshareDialogOpen] = useState(false);
  const [companyToUnshare, setCompanyToUnshare] = useState<RoleCompanyDto | null>(null);

  const { data: roleCompaniesResponse, isLoading, refetch } = useRolesGetRoleCompanies(roleId!, {
    query: {
      enabled: !!roleId && isSuperAdmin
    }
  });

  const sharedCompanies = Array.isArray(roleCompaniesResponse?.data)
    ? roleCompaniesResponse.data
    : Array.isArray(roleCompaniesResponse)
    ? roleCompaniesResponse
    : [];

  const sharedCompaniesCount = sharedCompanies.length;
  const displayedOtherOrganizationsCount = Math.max(0, otherOrganizationsCount ?? 0);

  const updateAccessMutation = useRolesUpdateRoleCompanyAccess({
    mutation: {
      onSuccess: () => {
        dispatch(
          addInfo({
            infoText: translate(
              nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.updateAccess)
            )
          })
        );
        refetch();
        onRefresh?.();
        handleCloseAccessDialog();
      },
      onError: (error: any) => {
        dispatch(
          addError({
            errorText:
              error?.message ||
              translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleShareError))
          })
        );
      }
    }
  });

  const unshareMutation = useRolesUnshareRole({
    mutation: {
      onSuccess: () => {
        dispatch(
          addInfo({
            infoText: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleUnshared), {
              roleName: roleName || '',
              companyName: companyToUnshare ? getCompanyName(companyToUnshare.companyId) : ''
            })
          })
        );
        refetch();
        onRefresh?.();
        handleCloseUnshareDialog();
      },
      onError: (error: any) => {
        dispatch(
          addError({
            errorText:
              error?.message ||
              translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.roleShareError))
          })
        );
      }
    }
  });

  const getCompanyName = (companyId?: number) => {
    if (!companyId) return '';
    const company = companies.find((item) => item.id === companyId);
    return company?.name || `#${companyId}`;
  };

  const getAccessLabel = (accessLevel?: AccessLevel) => {
    switch (accessLevel) {
      case AccessLevel.CanEdit:
        return {
          icon: <EditIcon fontSize="small" sx={{ mr: 0.5 }} />,
          text: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.canEdit))
        };
      case AccessLevel.CanAdministrate:
        return {
          icon: <AdminIcon fontSize="small" sx={{ mr: 0.5 }} />,
          text: translate(
            nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.canAdministrate)
          )
        };
      default:
        return {
          icon: <ViewIcon fontSize="small" sx={{ mr: 0.5 }} />,
          text: translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.canRead))
        };
    }
  };

  const handleOpenAccessDialog = (company: RoleCompanyDto) => {
    setSelectedCompany(company);
    setSelectedAccess(company.access || AccessLevel.CanRead);
    setIsAccessDialogOpen(true);
  };

  const handleCloseAccessDialog = () => {
    setIsAccessDialogOpen(false);
    setSelectedCompany(null);
  };

  const handleOpenUnshareDialog = (company: RoleCompanyDto) => {
    setCompanyToUnshare(company);
    setIsUnshareDialogOpen(true);
  };

  const handleCloseUnshareDialog = () => {
    setCompanyToUnshare(null);
    setIsUnshareDialogOpen(false);
  };

  const handleUpdateAccess = () => {
    if (!roleId || !selectedCompany?.companyId) return;

    updateAccessMutation.mutate({
      id: roleId,
      companyId: selectedCompany.companyId,
      data: selectedAccess
    });
  };

  const handleConfirmUnshare = () => {
    if (!roleId || !companyToUnshare?.companyId) return;

    unshareMutation.mutate({
      id: roleId,
      data: { companyId: companyToUnshare.companyId }
    });
  };

  if (!isSuperAdmin) {
    if (displayedOtherOrganizationsCount === 0) {
      return null;
    }

    return (
      <Alert severity="info" icon={<ShareIcon />}>
        <Typography variant="body2">
          {translate(
            nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.sharedCompaniesCount),
            { count: displayedOtherOrganizationsCount }
          )}
        </Typography>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (sharedCompanies.length === 0) {
    return (
      <Alert severity="info" icon={<ShareIcon />}>
        <Typography variant="body2">
          {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.notSharedWithCompany))}
        </Typography>
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          <ShareIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
          {translate(
            nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.sharedCompaniesCount),
            { count: sharedCompaniesCount }
          )}
        </Typography>
        <TableContainer component={Paper} elevation={0} variant="outlined">
          <Table size="small" aria-label="Shared companies table">
            <TableHead>
              <TableRow>
                <TableCell>
                  {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.company))}
                </TableCell>
                <TableCell>
                  {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.accessLevel))}
                </TableCell>
                {canManageSharing && (
                  <TableCell align="right">
                    {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.actions))}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {sharedCompanies.map((company) => {
                const { icon, text } = getAccessLabel(company.access);
                return (
                  <TableRow hover key={company.companyId}>
                    <TableCell component="th" scope="row">
                      {getCompanyName(company.companyId)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {icon}
                        {text}
                      </Box>
                    </TableCell>
                    {canManageSharing && (
                      <TableCell align="right">
                        <Tooltip
                          title={translate(
                            nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.updateAccess)
                          )}
                        >
                          <IconButton size="small" onClick={() => handleOpenAccessDialog(company)}>
                            <EditAccessIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={translate(
                            nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.unshareRole)
                          )}
                        >
                          <IconButton size="small" color="error" onClick={() => handleOpenUnshareDialog(company)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={isAccessDialogOpen} onClose={handleCloseAccessDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.updateAccess))}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {getCompanyName(selectedCompany?.companyId)}
          </Typography>
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <RadioGroup value={selectedAccess} onChange={(e) => setSelectedAccess(e.target.value as AccessLevel)}>
              <FormControlLabel
                value={AccessLevel.CanRead}
                control={<Radio />}
                label={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.canRead))}
              />
              <FormControlLabel
                value={AccessLevel.CanEdit}
                control={<Radio />}
                label={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.canEdit))}
              />
              <FormControlLabel
                value={AccessLevel.CanAdministrate}
                control={<Radio />}
                label={translate(
                  nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.canAdministrate)
                )}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAccessDialog}>
            {translate('common:' + nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateAccess}
            disabled={updateAccessMutation.isLoading}
            startIcon={updateAccessMutation.isLoading ? <CircularProgress size={16} /> : null}
          >
            {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.updateAccess))}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isUnshareDialogOpen} onClose={handleCloseUnshareDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.unshareRole))}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will remove company access to this role.
          </Alert>
          <Typography variant="body2">Remove access for {getCompanyName(companyToUnshare?.companyId)}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnshareDialog}>
            {translate('common:' + nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmUnshare}
            disabled={unshareMutation.isLoading}
            startIcon={unshareMutation.isLoading ? <CircularProgress size={16} /> : null}
          >
            {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.roleManagement.unshareRole))}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RoleSharedCompaniesDisplay;
